// The signed agreements of ONE owner — a retainer or a job: the list, the upload, and the
// two-choice removal dialog. Controlled — the rows and the archived flag come from the
// caller, and so does every string that names which of the two it is (see below).
//
// Controlled rather than self-fetching because of where this now appears. On the
// client card it sits inside each retainer's fold, and a card with six retainers
// would otherwise fire six requests the moment the tab opened. Instead the tab makes
// ONE client-wide read and hands each block its own rows, so opening a fold costs
// nothing. The retainer screen has only one of these, so it wraps this in
// AgreementsSection, which does the fetching and owns the archive toggle.
//
// The owner is always KNOWN here — it is the fold you are inside, or the page you are
// on. That is why no row names its retainer or job and the upload form never asks which
// one: the answer is the context.
//
// OWNER-AGNOSTIC. `api` is the endpoint set for whichever kind of agreement this is (see
// api/index.js agreementApis) and `ownerId` the retainer or job it hangs off. A client's
// retainer contract and a supplier's job contract are listed, uploaded, archived and
// downloaded identically; only the endpoints differ, so only the endpoints are injected.
import { useState } from 'react';
import he from '../i18n/he';
import { messageForError } from '../i18n/errors';
import Button from './Button';
import AgreementList from './AgreementList';
import AgreementModal from './AgreementModal';
import FileRemoveModal from './FileRemoveModal';

// `status` replaces the LIST while a fetch is in flight or has failed, leaving the
// heading and its controls in place — blanking the whole block would take away the
// archive toggle that caused the reload.
// `empty` and `namePlaceholder` join `heading` as the copy that has to NAME the
// owner: an empty list and a name example are the two places the words "this retainer" /
// "this job" appear. Both default to the retainer's wording, which is what the client
// card's folds — retainers, every one of them — already relied on.
export default function AgreementsBlock({
  agreements, archived, ownerId, api, onChange, heading, headerActions, status,
  empty = he.empty.agreements, namePlaceholder = he.agreements.namePlaceholder,
}) {
  const [editing, setEditing] = useState(null);  // agreement row, {} for new, or null
  const [removing, setRemoving] = useState(null); // agreement row, or null

  const restore = async (row) => {
    try {
      await api.setArchived(row.id, false);
      onChange();
    } catch (e) {
      window.alert(messageForError(e));
    }
  };

  const afterRemoval = () => { setRemoving(null); onChange(); };

  return (
    <div className="agreement-block">
      <div className="agreement-head">
        <h3>{archived ? he.agreements.archiveTitle : heading}</h3>
        <div className="row">
          {headerActions}
          {/* Upload is offered on the live view only: adding a contract straight into
              the archive is not a thing anyone means to do. */}
          {!archived && (
            <Button small variant="ghost" onClick={() => setEditing({})}>
              {he.agreements.add}
            </Button>
          )}
        </div>
      </div>

      {status || (
        <AgreementList
          agreements={agreements}
          archived={archived}
          api={api}
          onEdit={(r) => setEditing(r)}
          onRemove={(r) => setRemoving(r)}
          onRestore={restore}
          empty={{ text: archived ? he.empty.agreementsArchive : empty }}
        />
      )}

      {editing && (
        <AgreementModal
          agreement={editing}
          ownerId={ownerId}
          api={api}
          namePlaceholder={namePlaceholder}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChange(); }}
        />
      )}

      {/* Archive or destroy — the same dialog a business document gets, because it is
          the same choice: one of the two options may be removing the only copy of a
          signed contract that exists. */}
      {removing && (
        <FileRemoveModal
          labels={he.agreements}
          name={removing.title}
          fileName={removing.original_name}
          onClose={() => setRemoving(null)}
          onArchive={async () => {
            await api.setArchived(removing.id, true);
            afterRemoval();
          }}
          onDelete={async () => { await api.remove(removing.id); afterRemoval(); }}
        />
      )}
    </div>
  );
}
